import { useEffect, useState } from 'react'

import { default_locale } from '../context/locales'
import personData from '../data/personality-cards.json'
import traitData from '../data/trait-cards.json'

export interface PersonCard {
  id: string
  personalityTraits: string
  considerations: string[]
  name: string
  age: string
  image: string
}

export interface TraitCard {
  id: string
  description: string
  considerations: string[]
  stat?: string
  image: string
}

type PersonCardData = Record<string, PersonCard[]>
type TraitCardData = Record<string, TraitCard[]>

const typedPersonData = personData as PersonCardData
const typedTraitData = traitData as TraitCardData

function preloadImages(data: Array<{ image: string }>) {
  data.forEach((item) => {
    const image = new Image()
    image.src = item.image
  })
}

export interface UseCardPoolResult {
  person: PersonCard | null
  trait: TraitCard | null
  generateNewPerson: () => PersonCard
  generateNewTrait: () => TraitCard
  dealNewPair: () => { person: PersonCard; trait: TraitCard }
  resetPools: (newLocale: string) => void
}

export const useCardPool = (locale: string): UseCardPoolResult => {
  const [person, setPerson] = useState<PersonCard | null>(null)
  const [trait, setTrait] = useState<TraitCard | null>(null)
  const [personPool, setPersonPool] = useState<PersonCard[]>(
    typedPersonData[locale] || typedPersonData[default_locale],
  )
  const [traitPool, setTraitPool] = useState<TraitCard[]>(
    typedTraitData[locale] || typedTraitData[default_locale],
  )

  /**
   * Repopulate trait pool when empty
   */
  useEffect(() => {
    if (!traitPool?.length) {
      setTraitPool(typedTraitData[locale] || typedTraitData[default_locale])
    }
  }, [traitPool, locale])

  /**
   * Repopulate person pool when empty
   */
  useEffect(() => {
    if (!personPool?.length) {
      setPersonPool(typedPersonData[locale] || typedPersonData[default_locale])
    }
  }, [personPool, locale])

  /**
   * Preload images on initial load
   */
  useEffect(() => {
    preloadImages(typedPersonData.en_GB)
    preloadImages(typedTraitData.en_GB)
  }, [])

  /*
    Generate new cards will pick a card at random from the available pool of cards.
    It will then remove the specified card from the pool.
    Once the pool is empty, the hooks above will repopulate the pool with the full set of cards.
  */
  const generateNewPerson = (): PersonCard => {
    const newPerson =
      personPool[Math.floor(Math.random() * personPool.length)]
    setPersonPool(personPool.filter((item) => item.id !== newPerson.id))
    setPerson(newPerson)

    return newPerson
  }

  /**
   * Generate a new trait card
   */
  const generateNewTrait = (): TraitCard => {
    const newTrait = traitPool[Math.floor(Math.random() * traitPool.length)]
    setTraitPool(
      traitPool.filter((item) => item.description !== newTrait.description),
    )
    setTrait(newTrait)

    return newTrait
  }

  /**
   * Generate a new pair of cards (person and trait) in one call
   */
  const dealNewPair = () => {
    return {
      person: generateNewPerson(),
      trait: generateNewTrait(),
    }
  }

  /**
   * Reseed both pools from a new locale's data
   */
  const resetPools = (newLocale: string) => {
    setPersonPool(typedPersonData[newLocale] || typedPersonData[default_locale])
    setTraitPool(typedTraitData[newLocale] || typedTraitData[default_locale])
  }

  return {
    person,
    trait,
    generateNewPerson,
    generateNewTrait,
    dealNewPair,
    resetPools,
  }
}
